/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { type BaseRequestOptionsArgs, type FormGroup, type HttpResponse, type Polling, type Rest, Consts, HttpService, I18nService, PermissionService, PollingService, PromptService, SessionStorageService, UtilService } from '@dm/core';
import { INVALID_NASLOCKING_VALUE, MO } from 'app/common/constants';
import { TaskService, ToolService } from 'app/common/services';
import { type FileSystemResource, type HyperCdpSchedule, type WormLegalHold } from 'app/common/types/mo';
import { ProductConfig, ShardSwitch, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { RepType } from 'app/feature/service/file-service/file-system/file-system-str.service';
import { type CreateFsScanMissionPolicyCardComponent } from 'app/feature/settings/file-storage-service/antivirus/antivirus-policy/filesystem-scan-mission/create-fs-scan-mission-policy-card/create-fs-scan-mission-policy-card.component';
import { type CreatingAnOnAccessAntivirusScanTaskReq, type CreatingAnOnDemandAntivirusScanTaskReq, AntivirusStrService, DurationTypeE, ScanPolicyTypeE } from 'app/feature/settings/file-storage-service/antivirus/antivirus-str.service';
import { type Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { RemoteExecuteService } from './../../../../common/services/remote-execute.service';
import { type CommonAntivirusParams, type PoolVersionE } from './file-system-str.service';
import { type CapacityConversionItem } from './fs-model';

@Injectable({ providedIn: 'root' })
export class FileSystemService {
    constructor(
        private http: HttpService,
        private utilService: UtilService,
        private taskService: TaskService,
        private remoteExct: RemoteExecuteService,
        private pollingService: PollingService,
        private router: Router,
        private promptService: PromptService,
        private i18n: I18nService,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        private antivirusStr: AntivirusStrService,
        private toolService: ToolService,
    ) { }

    /**
     * 获取批量查询文件系统的rest
     */
    getBatchRest(): Rest {
        return this.http.makeUrl('fileSystem.getBatch', undefined, true);
    }

    /**
     * 查询单个文件系统
     * @param id
     */
    get(id: string, vstoreId?: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest);
    }

    /**
     * 创建文件系统
     * @param params
     * @param options
     */
    create(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.post');
        return this.http.post(rest, params, options);
    }

    createByOmtask(objName: string, params: FileSystemResource, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.post', 'V2');
        return this.taskService.post(SWITCH.IS_SIMPLE_MODE ? 'createFileShare' : 'createFileSystem', objName, rest, params, options);
    }

    /**
     * 修改文件系统
     * @param id
     * @param params
     * @param options
     */
    modify(id: string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.put').format(id);
        return this.http.put(rest, params, options);
    }

    /**
     * 删除文件系统
     * @param id
     * @param options
     */
    delete(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.delete').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.delete(rest, options);
    }

    // 获取所有多租户的Rest
    getVstoreRest(): Rest {
        return this.http.makeUrl('vstore.getBatch');
    }

    /**
     * 查询对应ID的租户
     */
    getVstore(id: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('vstore.get').format(id);
        return this.http.get(rest, options);
    }

    /**
     * 获取所有应用类型
     */
    getWorkloadTypes(version?: PoolVersionE, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        // templateType 0:LUN;1:文件系统
        const rest = this.http.makeUrl('workloadType.getBatch')
            .setParams({ isDetailInfo: true, templateType: '1' });
        if (version) {
            // 二级存储version以param下发,参数名改为poolVersion
            if (FeatureConfig.IsParamNameAsPoolVersionWhenGetWorkloadTypesInFS) {
                rest.setParams({
                    poolVersion: version,
                });
            } else {
                rest.setFilters([{
                    field: 'version',
                    matchMode: '::',
                    value: version,
                }]);
            }
        }

        // 无fs相关重删压缩license，过滤带重删压缩的应用类型
        if (ProductConfig.isCompressDedupFilterEnabled && !this.toolService.fsSupportDedupeAndCompression()) {
            rest.setFilters([{
                field: 'ENABLEDEDUP',
                matchMode: '::',
                value: false,
            }, {
                field: 'ENABLECOMPRESS',
                matchMode: '::',
                value: false,
            }]);
        }

        return this.http.get(rest, options);
    }

    /**
     * 查询对应ID的应用类型
     */
    getWorkloadType(id: string | number, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('workloadType.get').format(id)
            .setParams({ isDetailInfo: true });
        return this.http.get(rest, options);
    }

    /**
     * 获取智能推荐的文件系统数据
     */
    getSmartguiFilesystem(vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartgui.getFilesystem', 'V2').setParams({ vstoreId });
        return this.http.get(rest, options);
    }

    /**
     *  自适应计算容量单位
     *  @param capacity 容量（SECTOR）
     */
    getValueAndUnit(capacity: number): CapacityConversionItem {
        if (capacity >= 2048) {
            return this.utilService.adaptUnit(capacity, Consts.SECTOR);
        }
        // 容量低于1MB时用Blocks作为单位
        return {
            value: capacity,
            unit: Consts.SECTOR,
            label: `${capacity} Blocks`,
        };
    }

    getCDPSchedule(id: string): Observable<HyperCdpSchedule> {
        const rest = this.http.makeUrl('hyperCdpSchedule.get').format(id);
        return this.http.get(rest)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 根据租户获取双活租户pair
     */
    getVstorePair(vstoreID: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('vstoreHyperMetroPair.batch').setFilters([{
            field: 'LOCALVSTOREID',
            value: vstoreID,
            matchMode: '::',
        }]).setParams(
            { REPTYPE: RepType.HyperMetro },
        );
        // 二级存储不支持双活租户pair
        if (!ProductConfig.isSupportVstoreHyperMetroPair) {
            return of(null);
        }
        return this.http.get(rest);
    }

    /**
     * 获取远端设备ID
     */
    getFsHyperMetroDomain(domainId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fsHyperMetroDomain.get').format({
            ID: domainId,
        });

        return this.http.get(rest);
    }

    /**
     * 获取远端存储池列表
     */
    getRemoteStoragePoll(devId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('remoteExecute.post', 'V2');

        return this.http.post(rest, {
            device_id: devId,
            url: '/deviceManager/rest/xxx/storagepool',
            method: 'GET',
            body: {},
        });
    }

    /**
     * 关联查询智能缓存分区的信息
     * @param options
     */
    getAssociatePartition(params, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartCachePool.getAssociatePartition');
        rest.setParams(params);
        return this.http.get(rest, options);
    }

    /**
     * 查询缓存池中FS的rest
     */
    getSmartCachePartitionFsRest(partitionId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartCachePool.getAsscociateFs');
        rest.setParams({
            ASSOCIATEOBJTYPE: MO.SMARTCACHE_PARTITION,
            ASSOCIATEOBJID: partitionId,
        });
        return this.http.get(rest, options);
    }

    /**
     * 添加关联（Members）
     */
    addAsscociate(partitionId: string, fsId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartCachePool.addAsscociate');
        const body = { id: partitionId, associateObjType: MO.FILE_SYSTEM, associateObjID: fsId };
        return this.http.put(rest, body, options);
    }

    /**
     * 删除关联（Members）
     */
    removeAsscociate(body: { id: string; associateObjType: any; associateObjID: string }, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartCachePool.removeAsscociate');
        return this.http.put(rest, body, options);
    }

    /**
     * 查询全局worm时钟
     */
    getWormClock(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.getClock');
        return this.http.get(rest, { doException: false });
    }

    /**
     * 初始化全局worm时钟
     */
    initWormClock(initTime: number): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.initClock');
        return this.http.put(rest, { wormGlobalClock: initTime });
    }

    /**
     * 执行诉讼保留任务
     */
    doTask(taskParam: WormLegalHold): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.doTask');
        return this.http.post(rest, taskParam);
    }

    getFsMaxCap(fsId: string): Observable<HttpResponse> {
        return this.http.get(
            this.http.makeUrl('fileSystem.getMaxExpandCap')
                .setParams({ ID: fsId }),
            { doException: false },
        );
    }

    getRemoteFsMaxCap(fsId: string, remoteDeviceId: number): Observable<HttpResponse> {
        return this.remoteExct.get(
            remoteDeviceId,
            this.http.makeUrl('fileSystem.getMaxExpandCap')
                .setParams({ ID: fsId }),
            { doException: false },
        );
    }

    /**
     * 查询诉讼保留任务
     */
    getWormFileLegalHold(fsId: number): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.getWormFileLegalHold').setParams({ fileSystemId: fsId });
        return this.http.get(rest);
    }

    /**
     * 轮询查询诉讼保留任务
     */
    getWormFileLegalHoldPolling(fsId: number): Polling {
        const rest = this.http.makeUrl('fileSystem.getWormFileLegalHold').setParams({ fileSystemId: fsId });
        return this.pollingService.add(rest, 30);
    }

    /**
     * 删除诉讼保留任务
     */
    deleteWormFileLegalHold(fileSystemId: number, taskID: string, vstoreId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.deleteWormFileLegalHold').setParams({
            fileSystemId,
            taskID,
            vstoreId,
        });
        return this.http.delete(rest);
    }

    /**
     * 获取审计日志配置
     */
    getAuditLog(vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('auditLog.get');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }

        return this.http.get(rest, options);
    }

    /**
     * 获取审计日志开关是否开启
     */
    getAuditLogEnabled(vstoreId: string): Observable<boolean> {
        return this.getAuditLog(vstoreId, { doException: false }).pipe(
            map(({ data }) => data && data.runEnable === 'true'),
        );
    }

    /**
     * 跳转到设置-审计日志
     */
    jumpToAuditLog = (vstoreId: string, noPrompt?: boolean): void => {
        const jump = (): void => {
            this.sessionStorageService.set('vstoreId', vstoreId);
            this.router.navigate(['/settings/file-storage-service/audit-log'], { queryParams: { needRefreshVstore: true } });
        };

        if (noPrompt) {
            jump();
        } else {
            this.promptService.promptConfirm({
                title: this.i18n.get('common.leavePage_title'),
                content: this.i18n.get('common.cancleCurrentPage_leavePage_label'),
                onCancel: (() => {
                    this.promptService.close();
                }),
                onOk: (() => {
                    this.promptService.close();
                    jump();
                }),
            });
        }
    };

    /**
     * 检查NAS锁策略字段是否是无效值
     */
    isValidValue(value: string): boolean {
        let isValid = false;
        if (value && value !== INVALID_NASLOCKING_VALUE) {
            isValid = true;
        }
        return isValid;
    }

    /**
     * 查询防病毒服务器
     */
    getAntivirusServer(vstoreId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('antivirus.getServer');
        rest.setParams({ vstoreId });
        return this.http.get(rest);
    }

    /**
     *  展示防病毒的必要条件
     *
     * @returns boolean
     */
    showAntvirusNecessaryCondition(): boolean {
        return ShardSwitch.SHOW_ANTIVIRUS_IN_FS_CREATE &&
            ProductConfig.isSupportAntivirus &&
            this.permissionService.hasPermission('antivirus_W');
    }

    /**
     * 收集按需策略的参数
     *
     */
    collectOnDemandParams(
        ondemandCard: CreateFsScanMissionPolicyCardComponent,
        vm: FormGroup,
        commonAntivirusParams: CommonAntivirusParams,
    ): { av_on_demand: CreatingAnOnDemandAntivirusScanTaskReq } | { av_on_demand?: undefined } {
        if (ondemandCard?.switch) {
            const av_on_demand: CreatingAnOnDemandAntivirusScanTaskReq = {
                ...commonAntivirusParams,
                policyId: vm.get(`selectedScanPolicy${ScanPolicyTypeE.ONDEMAND}`).value,
            };

            if (vm.get('immediately').value) {
                av_on_demand.isImmediatelyTask = true;
                av_on_demand.immediatelyDuration = vm.get('durationType').value === DurationTypeE.CUSTOM ?
                    this.antivirusStr.getDuration(vm.get('durationHour').value, vm.get('durationMinute').value) :
                    DurationTypeE.UNTILFINISHED;
            }

            return { av_on_demand };
        }
        return {};
    }

    /**
     * 收集实时策略的参数
     *
     */
    collectOnAccessParams(
        onaccessCard: CreateFsScanMissionPolicyCardComponent,
        vm: FormGroup,
        commonAntivirusParams: CommonAntivirusParams,
    ): { av_on_access: CreatingAnOnAccessAntivirusScanTaskReq } | { av_on_access?: undefined } {
        if (onaccessCard?.switch) {
            const av_on_access: CreatingAnOnAccessAntivirusScanTaskReq = {
                ...commonAntivirusParams,
                policyId: vm.get(`selectedScanPolicy${ScanPolicyTypeE.ONACCESS}`).value,
            };

            return { av_on_access };
        }
        return {};
    }

    /**
     * 收集所有防病毒数据
     *
     */
    collectAntivirusData(
        ondemandCard: CreateFsScanMissionPolicyCardComponent,
        onaccessCard: CreateFsScanMissionPolicyCardComponent,
        vm: FormGroup,
        commonAntivirusParams: CommonAntivirusParams,
    ): any {
        return {
            ...this.collectOnDemandParams(ondemandCard, vm, commonAntivirusParams),
            ...this.collectOnAccessParams(onaccessCard, vm, commonAntivirusParams),
        };
    }
}
